import { LucideFoldVertical, LucideUnfoldVertical } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { Button } from '~/components/ui'
import { Listing, ListingLeaseSpace } from '~/models'
import { cn, fmtNumberAbbr, RATE_TYPES } from '~/utils'

type ListingLeaseSpacesProps = {
  className?: string
  listing: Listing
  leaseSpaces: ListingLeaseSpace[]
  isNumAbbr?: boolean
  headClassName?: string
  showAll?: boolean
}

export default function ListingLeaseSpaces({
  className,
  listing,
  leaseSpaces,
  isNumAbbr = false,
  headClassName = 'bg-background',
  showAll = false,
}: ListingLeaseSpacesProps) {
  const [expanded, setExpanded] = useState<{ [key: number]: boolean }>({})

  const sortedLeaseSpaces = useMemo(() => {
    // const spaces = showAll
    //   ? [...(listing.sortedLeaseSpaces || [])]
    //   : [...(listing.sortedLeaseSpaces || [])].filter((s) => s.available)
    leaseSpaces.sort((a, b) => a.modified.localeCompare(b.modified))
    return leaseSpaces
  }, [leaseSpaces])

  const headers = useMemo(() => {
    const headers = [
      { key: 'name', label: 'Name' },
      { key: 'rate', label: 'Rate' },
    ]
    if (
      sortedLeaseSpaces.some(
        (space) => space.minAvailableSf || space.maxAvailableSf
      )
    ) {
      headers.push({ key: 'sf', label: 'SF' })
    }
    if (sortedLeaseSpaces.some((space) => space.acres)) {
      headers.push({ key: 'acres', label: 'Acres' })
    }
    if (sortedLeaseSpaces.some((space) => space.formerUse)) {
      headers.push({ key: 'formerUse', label: 'Former Use' })
    }
    if (sortedLeaseSpaces.some((space) => space.sublease)) {
      headers.push({ key: 'sublease', label: 'Sublease' })
    }
    if (showAll) {
      headers.push({ key: 'available', label: 'Available' })
    }
    // headers.push({ key: 'dom', label: 'DOM' })
    return headers
  }, [])

  const hasExpanded = useMemo(
    () => sortedLeaseSpaces.some((s) => s.description),
    [sortedLeaseSpaces]
  )

  const maybeFmtNumberAbbr = (value?: number) => {
    if (isNumAbbr) {
      return fmtNumberAbbr(value)
    }
    return value?.toLocaleString()
  }

  const renderSf = (space: ListingLeaseSpace) => {
    const min = space.minAvailableSf
    const max = space.maxAvailableSf
    if (!min && !max) {
      return null
    } else if (min !== max) {
      return `${maybeFmtNumberAbbr(min)} - ${maybeFmtNumberAbbr(max)} SF`
    }
    return `${maybeFmtNumberAbbr(min)} SF`
  }

  const renderRate = (space: ListingLeaseSpace) => {
    const min = space.minRate
    const max = space.maxRate
    const rateType =
      space.rateType && typeof RATE_TYPES[space.rateType] !== 'undefined'
        ? ` /${RATE_TYPES[space.rateType]}`
        : ''
    if (!min && !max) {
      return 'Negotiable'
    } else if (min && !max) {
      return `$${maybeFmtNumberAbbr(min)} ${rateType}`
    } else if (min && max && min !== max) {
      return `$${maybeFmtNumberAbbr(min)} - $${maybeFmtNumberAbbr(
        max
      )}${rateType}`
    }
    return `$${maybeFmtNumberAbbr(min)}${rateType}`
  }

  const renderAcres = (space: ListingLeaseSpace) => {
    const acres = space.acres
    if (!acres) {
      return null
    }
    return `${acres?.toLocaleString()} ac`
  }

  if (sortedLeaseSpaces.length === 0) {
    return null
  }

  return (
    <div
      className={cn(
        className,
        'relative max-h-72 overflow-x-auto overflow-y-auto'
      )}>
      <table className="min-w-full divide-y divide-border">
        <thead className={cn(headClassName, 'sticky top-0')}>
          <tr>
            {hasExpanded && <th />}
            {headers.map(({ key, label }, index) => (
              <th
                key={key}
                className={cn(
                  'pb-1 pl-2 pr-2 text-left font-medium text-foreground',
                  index === 0 && 'min-w-[80px] pl-0'
                )}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sortedLeaseSpaces.map((space, index) => {
            // const dom = getDom(space.created)

            return (
              <React.Fragment key={index}>
                <tr>
                  {hasExpanded && (
                    <td>
                      {space.description && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 p-1"
                          onClick={() =>
                            setExpanded({
                              ...expanded,
                              [index]: !expanded[index],
                            })
                          }>
                          {expanded[index] ? (
                            <LucideFoldVertical className="h-4 w-4" />
                          ) : (
                            <LucideUnfoldVertical className="h-4 w-4" />
                          )}
                          <span className="sr-only">Fold</span>
                        </Button>
                      )}
                    </td>
                  )}
                  <td className="truncate whitespace-nowrap py-2 pr-2 font-medium text-foreground">
                    {space.name || listing.name}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2  text-foreground">
                    {renderRate(space)}
                  </td>
                  {sortedLeaseSpaces.filter(
                    (space) => space.minAvailableSf || space.maxAvailableSf
                  ).length > 0 && (
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {renderSf(space)}
                    </td>
                  )}
                  {sortedLeaseSpaces.filter((space) => space.acres).length >
                    0 && (
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {renderAcres(space)}
                    </td>
                  )}
                  {sortedLeaseSpaces.filter((space) => space.formerUse).length >
                    0 && (
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {space.formerUse}
                    </td>
                  )}
                  {sortedLeaseSpaces.filter((space) => space.sublease).length >
                    0 && (
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {space.sublease ? 'Yes' : 'No'}
                    </td>
                  )}
                  {showAll && (
                    <td className="whitespace-nowrap px-2 py-2 text-foreground">
                      {space.available && listing.status !== 'leased'
                        ? 'Yes'
                        : 'No'}
                    </td>
                  )}
                  {/* <td className="whitespace-nowrap px-2 py-2 text-foreground">
                  {dom.toLocaleString()}
                </td> */}
                </tr>
                {hasExpanded && expanded[index] && space.description && (
                  <td colSpan={headers.length + 1}>
                    <div
                      className="prose max-w-none py-4 pl-7 pr-4 lg:pl-9 xl:pl-10"
                      dangerouslySetInnerHTML={{ __html: space.description }}
                    />
                  </td>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
